/**
 * Markdown transformers for Lexical editor.
 * Handles conversion between Lexical nodes and markdown syntax.
 */

import { TRANSFORMERS, $convertFromMarkdownString, TextMatchTransformer, ElementTransformer } from '@lexical/markdown';
import {
  TableNode,
  TableRowNode,
  TableCellNode,
  $createTableNode,
  $createTableRowNode,
  $createTableCellNode,
  $isTableNode,
  $isTableRowNode,
  $isTableCellNode,
  TableCellHeaderStates,
} from '@lexical/table';
import { $createTextNode, $createParagraphNode, $createLineBreakNode, $getRoot, $isTextNode, $isParagraphNode, $isLineBreakNode, LexicalNode, TextNode } from 'lexical';
import {
  $createHorizontalRuleNode,
  $isHorizontalRuleNode,
  HorizontalRuleNode,
} from '@lexical/react/LexicalHorizontalRuleNode';
import { $createImageNode, $isImageNode, ImageNode } from './nodes/ImageNode';
import { $createPageBreakNode, $isPageBreakNode, PageBreakNode } from './nodes/PageBreakNode';
import {
  PROTECTED_SECTION_TRANSFORMER,
  preprocessProtectedSections,
} from './transformers/protected-section-transformer';
import { $createProtectedSectionNode } from './nodes/ProtectedSectionNode';

/**
 * Custom transformer for images with markdown syntax: ![alt](url)
 * Uses both TextMatchTransformer (for typing and export) and ElementTransformer (for import)
 */
const IMAGE_TRANSFORMER: TextMatchTransformer = {
  dependencies: [ImageNode],
  export: (node) => {
    if (!$isImageNode(node)) {
      return null;
    }
    const altText = node.getAltText();
    const src = node.getSrc();
    return `![${altText}](${src})`;
  },
  importRegExp: /!\[([^\]]*)\]\(([^)]+)\)/,
  regExp: /!\[([^\]]*)\]\(([^)]+)\)$/,
  replace: (textNode, match) => {
    const [, altText, src] = match;
    const imageNode = $createImageNode({
      src,
      altText: altText || '',
    });
    textNode.replace(imageNode);
  },
  trigger: ')',
  type: 'text-match',
};

/**
 * ElementTransformer for importing standalone image lines during markdown parse
 */
const IMAGE_ELEMENT_TRANSFORMER: ElementTransformer = {
  dependencies: [ImageNode],
  export: (node) => {
    // Export is handled by TEXT_MATCH transformer above
    return null;
  },
  regExp: /^!\[([^\]]*)\]\(([^)]+)\)$/,
  replace: (parentNode, _children, match) => {
    const [, altText, src] = match;
    const imageNode = $createImageNode({
      src,
      altText: altText || '',
    });
    parentNode.append(imageNode);
  },
  type: 'element',
};

/**
 * Custom transformer for page breaks (***)
 */
const PAGE_BREAK_TRANSFORMER: ElementTransformer = {
  dependencies: [PageBreakNode],
  export: (node) => {
    if (!$isPageBreakNode(node)) return null;
    return '***';
  },
  regExp: /^\*\*\*[\s]*$/,
  replace: (parentNode) => {
    const pageBreakNode = $createPageBreakNode();
    parentNode.replace(pageBreakNode);
  },
  type: 'element',
};

/**
 * Custom transformer for horizontal rules (--- and ___)
 */
const HORIZONTAL_RULE_TRANSFORMER: ElementTransformer = {
  dependencies: [HorizontalRuleNode],
  export: (node) => {
    if (!$isHorizontalRuleNode(node)) return null;
    return '---';
  },
  regExp: /^(?:---|___)[\s]*$/,
  replace: (parentNode) => {
    const hrNode = $createHorizontalRuleNode();
    parentNode.replace(hrNode);
  },
  type: 'element',
};

/**
 * Helper to parse a GFM table row into cells
 */
function parseTableRow(line: string): string[] {
  const trimmed = line.trim();
  const withoutPipes = trimmed.startsWith('|') ? trimmed.slice(1) : trimmed;
  const withoutEndPipe = withoutPipes.endsWith('|') ? withoutPipes.slice(0, -1) : withoutPipes;
  return withoutEndPipe.split('|').map((cell) => cell.trim());
}

/**
 * Check if a line is a table separator row (e.g., |---|---|)
 */
function isTableSeparator(line: string): boolean {
  return /^\|?[\s-:|]+\|?$/.test(line.trim());
}

/**
 * Check if a line looks like a table row
 */
function isTableRow(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length > 2;
}

/**
 * Serialize a Lexical node to markdown string
 * Handles text nodes (with formatting), image nodes, line breaks, etc.
 */
function serializeNodeToMarkdown(node: LexicalNode): string {
  // Handle line breaks - convert to <br> for table cells
  if ($isLineBreakNode(node)) {
    return '<br>';
  }

  if ($isTextNode(node)) {
    let text = node.getTextContent();
    const format = node.getFormat();

    // Apply inline code formatting (backticks)
    // The format is a bitmask - code is bit 4 (value 16)
    const IS_CODE = 16;
    if (format & IS_CODE) {
      text = `\`${text}\``;
    }

    // Apply bold formatting
    const IS_BOLD = 1;
    if (format & IS_BOLD) {
      text = `**${text}**`;
    }

    // Apply italic formatting
    const IS_ITALIC = 2;
    if (format & IS_ITALIC) {
      text = `*${text}*`;
    }

    return text;
  }
  if ($isImageNode(node)) {
    const altText = node.getAltText();
    const src = node.getSrc();
    return `![${altText}](${src})`;
  }
  // For other nodes, try to get text content
  return node.getTextContent();
}

/**
 * Serialize table cell content to markdown
 * Properly handles images, text, and line breaks within cells
 */
function serializeCellToMarkdown(cell: TableCellNode): string {
  const cellChildren = cell.getChildren();
  const parts: string[] = [];

  for (let i = 0; i < cellChildren.length; i++) {
    const child = cellChildren[i];
    let partContent = '';

    if ($isParagraphNode(child)) {
      // Paragraph contains the actual content nodes
      const paragraphChildren = child.getChildren();
      for (const node of paragraphChildren) {
        partContent += serializeNodeToMarkdown(node);
      }
    } else {
      // Direct child node
      partContent += serializeNodeToMarkdown(child);
    }

    if (partContent) {
      parts.push(partContent);
    }
  }

  // Join multiple paragraphs with <br>
  return parts.join('<br>').trim();
}

/**
 * Custom transformer for GFM tables (export only)
 * Import is handled by preprocessMarkdownTables below
 */
const TABLE_TRANSFORMER: ElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  export: (node) => {
    if (!$isTableNode(node)) return null;

    const rows = node.getChildren();
    if (rows.length === 0) return '';

    let markdown = '';
    let isFirstRow = true;

    rows.forEach((row) => {
      if (!$isTableRowNode(row)) return;

      const cells = row.getChildren();
      const cellTexts = cells.map((cell) => {
        if (!$isTableCellNode(cell)) return '';
        // Use serializeCellToMarkdown to properly handle images and other nodes
        return serializeCellToMarkdown(cell);
      });

      markdown += '| ' + cellTexts.join(' | ') + ' |\n';

      // Add separator after header row
      if (isFirstRow) {
        markdown += '|' + cellTexts.map(() => ' --- ').join('|') + '|\n';
        isFirstRow = false;
      }
    });

    return markdown.trimEnd();
  },
  // This regex won't match during import since tables span multiple lines
  // Import is handled by preprocessMarkdownTables
  regExp: /^__LEXICAL_TABLE_PLACEHOLDER__$/,
  replace: () => {
    // Import handled elsewhere
  },
  type: 'element',
};

/**
 * Parse inline formatting (bold, italic, code) and create text nodes
 * Returns array of text nodes with appropriate formatting applied
 */
function parseInlineFormatting(text: string): TextNode[] {
  const nodes: TextNode[] = [];

  // Combined regex to match inline code, bold, or italic
  // Order matters: check ** before * to avoid partial matches
  // Matches: `code`, **bold**, *italic*
  const formatRegex = /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*]+)\*/g;

  let lastIndex = 0;
  let match;

  while ((match = formatRegex.exec(text)) !== null) {
    // Add text before the formatted segment
    if (match.index > lastIndex) {
      const textBefore = text.slice(lastIndex, match.index);
      if (textBefore) {
        nodes.push($createTextNode(textBefore));
      }
    }

    // Determine which capture group matched
    if (match[1] !== undefined) {
      // Inline code: `code`
      const codeNode = $createTextNode(match[1]);
      codeNode.toggleFormat('code');
      nodes.push(codeNode);
    } else if (match[2] !== undefined) {
      // Bold: **bold**
      const boldNode = $createTextNode(match[2]);
      boldNode.toggleFormat('bold');
      nodes.push(boldNode);
    } else if (match[3] !== undefined) {
      // Italic: *italic*
      const italicNode = $createTextNode(match[3]);
      italicNode.toggleFormat('italic');
      nodes.push(italicNode);
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last formatted segment
  if (lastIndex < text.length) {
    const textAfter = text.slice(lastIndex);
    if (textAfter) {
      nodes.push($createTextNode(textAfter));
    }
  }

  // If no formatting found, return original text as single node
  if (nodes.length === 0 && text) {
    nodes.push($createTextNode(text));
  }

  return nodes;
}

/**
 * Parse a text segment for inline code (backticks) and create text nodes
 * Returns array of text nodes, some with code formatting applied
 * @deprecated Use parseInlineFormatting instead
 */
function _parseInlineCode(text: string): TextNode[] {
  return parseInlineFormatting(text);
}

/**
 * Parse a text segment for inline formatting and images
 * Handles: images ![alt](src), bold **text**, italic *text*, code `text`
 */
function parseTextSegment(text: string): LexicalNode[] {
  const nodes: LexicalNode[] = [];
  // Match image syntax: ![alt](src)
  const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;

  let lastIndex = 0;
  let match;

  while ((match = imageRegex.exec(text)) !== null) {
    // Add text before the image (with inline formatting)
    if (match.index > lastIndex) {
      const textBefore = text.slice(lastIndex, match.index);
      if (textBefore) {
        const textNodes = parseInlineFormatting(textBefore);
        nodes.push(...textNodes);
      }
    }

    // Add the image node
    const [, altText, src] = match;
    nodes.push($createImageNode({ src, altText: altText || '' }));

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last image (with inline formatting)
  if (lastIndex < text.length) {
    const textAfter = text.slice(lastIndex);
    if (textAfter) {
      const textNodes = parseInlineFormatting(textAfter);
      nodes.push(...textNodes);
    }
  }

  return nodes;
}

/**
 * Parse cell content and create appropriate Lexical nodes
 * Handles: <br> tags, images, bold, italic, inline code
 */
function parseCellContent(cellText: string): LexicalNode[] {
  const nodes: LexicalNode[] = [];

  // Split on <br> or <br/> or <br /> tags (case insensitive)
  const segments = cellText.split(/<br\s*\/?>/gi);

  segments.forEach((segment, index) => {
    // Parse the text segment for images and inline formatting
    const segmentNodes = parseTextSegment(segment);
    nodes.push(...segmentNodes);

    // Add line break after each segment except the last
    if (index < segments.length - 1) {
      nodes.push($createLineBreakNode());
    }
  });

  // If no content, add empty text node
  if (nodes.length === 0) {
    nodes.push($createTextNode(cellText));
  }

  return nodes;
}

/**
 * Create a table node from parsed table data
 */
function createTableFromData(rows: string[][]): TableNode {
  const tableNode = $createTableNode();

  rows.forEach((cells, rowIndex) => {
    const rowNode = $createTableRowNode();

    cells.forEach((cellText) => {
      // First row is header
      const headerState = rowIndex === 0 ? TableCellHeaderStates.ROW : TableCellHeaderStates.NO_STATUS;
      const cellNode = $createTableCellNode(headerState);
      const paragraph = $createParagraphNode();

      // Parse cell content for images and text
      const contentNodes = parseCellContent(cellText);
      contentNodes.forEach((node) => paragraph.append(node));

      cellNode.append(paragraph);
      rowNode.append(cellNode);
    });

    tableNode.append(rowNode);
  });

  return tableNode;
}

/**
 * Parse GFM table markdown into array of rows
 */
function parseTableMarkdown(tableLines: string[]): string[][] {
  const rows: string[][] = [];

  for (const line of tableLines) {
    // Skip separator rows
    if (isTableSeparator(line)) continue;

    const cells = parseTableRow(line);
    if (cells.length > 0) {
      rows.push(cells);
    }
  }

  return rows;
}

/**
 * Extract tables from raw markdown and replace with placeholders.
 * Returns the modified markdown and a map of placeholder -> table data.
 *
 * Uses a placeholder format that won't be interpreted as markdown formatting.
 * The format [LEXICAL-TABLE-N] avoids double underscores (which become bold).
 */
function extractTablesFromMarkdown(markdown: string): {
  processedMarkdown: string;
  tables: Map<string, string[][]>;
} {
  const lines = markdown.split('\n');
  const tables = new Map<string, string[][]>();
  const outputLines: string[] = [];
  let tableCounter = 0;

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Check if this line starts a table
    if (isTableRow(line)) {
      const tableLines: string[] = [line];
      let j = i + 1;

      // Collect all consecutive table lines (including separator)
      while (j < lines.length) {
        const nextLine = lines[j];
        const trimmed = nextLine.trim();

        if (trimmed && (isTableRow(trimmed) || isTableSeparator(trimmed))) {
          tableLines.push(nextLine);
          j++;
        } else if (trimmed === '') {
          // Empty line ends the table
          break;
        } else {
          // Non-table content ends the table
          break;
        }
      }

      // Only treat as table if we have at least header + separator (2 lines)
      if (tableLines.length >= 2) {
        const tableData = parseTableMarkdown(tableLines);
        if (tableData.length > 0) {
          // Use format that won't be interpreted as markdown (no __ or **)
          const placeholder = `LEXICALTABLE${tableCounter}PLACEHOLDER`;
          tables.set(placeholder, tableData);
          outputLines.push(placeholder);
          tableCounter++;
          i = j;
          continue;
        }
      }
    }

    outputLines.push(line);
    i++;
  }

  return {
    processedMarkdown: outputLines.join('\n'),
    tables,
  };
}

/**
 * Import markdown with table support
 * Pre-processes tables from raw markdown string BEFORE Lexical parses it,
 * ensuring image markdown syntax is preserved in table cells.
 */
export function importMarkdownWithTables(markdown: string): void {
  // Step 0: Clean up any orphaned placeholders from previous broken imports
  // This handles cases where LEXICALTABLE#PLACEHOLDER was saved to the database
  let cleanedMarkdown = markdown.replace(/LEXICALTABLE\d+PLACEHOLDER\n?/g, '');
  cleanedMarkdown = cleanedMarkdown.replace(/LEXICALPROTECTED\d+SECTION\n?/g, '');

  // Step 1: Extract protected sections from raw markdown
  const { processedMarkdown: markdownWithoutProtected, protectedSections } =
    preprocessProtectedSections(cleanedMarkdown);

  // Step 2: Extract tables from raw markdown and replace with placeholders
  const { processedMarkdown, tables } = extractTablesFromMarkdown(markdownWithoutProtected);

  // Step 3: Parse the markdown without tables or protected sections (placeholders remain as text)
  $convertFromMarkdownString(processedMarkdown, MARKDOWN_TRANSFORMERS);

  // Step 4: Find placeholders and replace with actual nodes
  const root = $getRoot();
  const children = root.getChildren();

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    const textContent = child.getTextContent().trim();

    // Check if this node is a table placeholder (format: LEXICALTABLENPLACEHOLDER)
    const tableMatch = textContent.match(/^LEXICALTABLE(\d+)PLACEHOLDER$/);
    if (tableMatch && tables.size > 0) {
      const tableData = tables.get(textContent);
      if (tableData) {
        const tableNode = createTableFromData(tableData);
        child.replace(tableNode);
      }
    }

    // Check if this node is a protected section placeholder (format: LEXICALPROTECTEDNSECTION)
    const protectedMatch = textContent.match(/^LEXICALPROTECTED(\d+)SECTION$/);
    if (protectedMatch && protectedSections.size > 0) {
      const sectionData = protectedSections.get(textContent);
      if (sectionData) {
        const protectedNode = $createProtectedSectionNode(
          sectionData.heading,
          sectionData.placeholder
        );
        child.replace(protectedNode);
      }
    }
  }
}

/**
 * All markdown transformers including custom ones
 * Both transformers are needed: TextMatchTransformer for export and typing,
 * ElementTransformer for parsing standalone image lines during import
 */
export const MARKDOWN_TRANSFORMERS = [
  PROTECTED_SECTION_TRANSFORMER, // Protected sections first for highest priority
  TABLE_TRANSFORMER, // Table transformer second for priority
  PAGE_BREAK_TRANSFORMER, // Page break before horizontal rule for *** priority
  HORIZONTAL_RULE_TRANSFORMER,
  ...TRANSFORMERS,
  IMAGE_TRANSFORMER,
  IMAGE_ELEMENT_TRANSFORMER,
];
